import * as React from 'react';
import { AlertCircle, Check } from 'lucide-react';

import { cn } from 'lib/utils';

/**
 * Puerto real de `ProcessTimeline.tsx` (chirigota, `src/components/MiBeneficioV2/`,
 * usado por `IndividualDashboardV2.tsx` y `MiBeneficioV2.tsx` para el estado del
 * traspaso/apertura de plan) — DISTINTO de `ui/Steps` (puerto de `StepIndicator.tsx`,
 * sin CTA ni fecha, y ahora también vertical, con su mismo tamaño): este lleva círculo relleno +
 * conector, fecha en el
 * paso completado, y un botón de acción en el paso activo ("Verificar identidad →").
 * No se fusionan en uno: son dos componentes reales distintos en producción, con
 * anatomías distintas (uno es indicador puro, el otro lleva interacción).
 *
 * Solo la PARTE VISUAL/interacción se porta aquí — el cálculo real de qué paso está
 * en qué estado (`computeTimelineSteps`, en el archivo real) depende de datos de
 * backend (estado del traspaso, estado KYC) que no tiene sentido reproducir como
 * lógica de UI: cada pantalla que use esto construye su propio array `steps` con el
 * estado ya resuelto.
 *
 * BUG REAL CORREGIDO: el `StepCircle` real trata ACTIVE y ERROR con el MISMO círculo
 * (`bg-primary` relleno con pulso, sin distinguirlos) — ni siquiera con el color
 * rosa/verde que se corrigió en otros sitios de este sistema, aquí directamente NO
 * hay ninguna diferencia visual entre "en curso, todo bien" y "rechazado, hace falta
 * acción". Aquí: ERROR → `destructive` (rojo, sin el pulso — un error no está "en
 * progreso"), con icono `AlertCircle` en vez del punto relleno — mismo criterio que
 * `ui/Steps` (el color nunca es la única señal).
 */
export type ProcessStepState = 'completed' | 'active' | 'pending' | 'error';

export interface ProcessStep {
	id: string;
	label: string;
	sublabel?: string;
	state: ProcessStepState;
	/** Solo se pinta si state === 'completed' (real: fecha de cuándo se completó ese paso). */
	date?: string;
	/**
	 * Texto del botón de acción — se pinta en cualquier estado, siempre que haya
	 * `onStepCtaClick`. No solo en `active`: el real ofrece «Empezar a gestionar mi plan →»
	 * sobre el paso YA COMPLETADO, cuando el traspaso termina. Quién puede accionar lo
	 * decide el que construye los pasos, no este componente.
	 */
	ctaLabel?: string;
}

export interface ProcessTimelineProps {
	steps: ProcessStep[];
	onStepCtaClick?: (stepId: string) => void;
	/**
	 * Si se pasa, emite `data-testid`: `<prefijo>-step-<id>` en la etiqueta y
	 * `<prefijo>-cta-<id>` en el botón. Por defecto no emite ninguno.
	 *
	 * Está aquí porque la alternativa era peor: la aplicación que lo consume ya tenía una
	 * red de tests colgada de esos ganchos, y reescribirla a la vez que se cambia el
	 * componente deja sin verificar las dos cosas al mismo tiempo. El prefijo lo elige
	 * quien consume, así que el sistema no carga con los nombres de nadie.
	 */
	testIdPrefix?: string;
	className?: string;
}

const StepCircle = ({ state }: { state: ProcessStepState }) => {
	if (state === 'completed') {
		return (
			<div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
				<Check className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
			</div>
		);
	}
	if (state === 'error') {
		return (
			<div className="w-8 h-8 rounded-full bg-destructive flex items-center justify-center shrink-0">
				<AlertCircle className="w-4 h-4 text-destructive-foreground" />
			</div>
		);
	}
	if (state === 'active') {
		return (
			<div
				className="animate-pulse-soft w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0"
				style={{ boxShadow: 'var(--shadow-glow-emerald)' }}
			>
				<div className="w-2.5 h-2.5 rounded-full bg-primary-foreground" />
			</div>
		);
	}
	return (
		<div className="w-8 h-8 rounded-full border-2 border-muted flex items-center justify-center shrink-0">
			<div className="w-2 h-2 rounded-full bg-muted" />
		</div>
	);
};

const Connector = ({
	fromState,
	toState
}: {
	fromState: ProcessStepState;
	toState: ProcessStepState;
}) => {
	const bothPast = fromState === 'completed' && (toState === 'completed' || toState === 'active');
	return (
		<div className="flex justify-center w-8 shrink-0">
			<div
				className={cn(
					'w-0.5 h-6',
					bothPast ? 'bg-primary' : 'border-l-2 border-dashed border-muted'
				)}
			/>
		</div>
	);
};

// 🔴 `--primary-text`, no `--primary`: aquí el verde de marca hace de PRIMER PLANO, y
// `--primary` está afinado para ser relleno. Sobre la tarjeta clara da 3,14:1, por debajo del
// 4,5:1 de AA. `--primary-text` da 7,35:1 en claro y en oscuro vale exactamente lo que vale
// `--primary` hoy, así que el tema oscuro no se mueve.
const LABEL_COLOR: Record<ProcessStepState, string> = {
	completed: 'text-primary-text',
	active: 'text-foreground',
	error: 'text-destructive-text',
	pending: 'text-muted-foreground'
};

/**
 * `animate-pulse-soft` y `bg-gradient-brand` (el CTA) son utilidades de `index.css`:
 * este componente cuenta con que existan, igual que el real.
 *
 * @deprecated PROPUESTA DE RETIRADA, todavía NO se ha quitado (ver «Propuesta de retirada» en su
 * story `ui/ProcessTimeline`). Las operativas destacadas (`ui/CardAction`) y el estado de los
 * traspasos (`ui/TransferStatusCard`) cubren lo que hacía, y `ui/Steps` en vertical ya tiene su
 * mismo tamaño y aspecto. No lo uses en pantallas nuevas.
 */
export const ProcessTimeline = ({
	steps,
	onStepCtaClick,
	testIdPrefix,
	className
}: ProcessTimelineProps) => (
	<div className={cn('space-y-0', className)}>
		{steps.map((step, idx) => (
			<div key={step.id}>
				<div className="flex items-start gap-4">
					<StepCircle state={step.state} />
					<div className="pb-1 min-w-0 flex-1">
						<p
							className={cn('text-sm font-semibold leading-tight', LABEL_COLOR[step.state])}
							data-testid={testIdPrefix && `${testIdPrefix}-step-${step.id}`}
						>
							{step.label}
							{step.state === 'completed' && step.date && (
								<span className="ml-2 text-xs font-normal text-muted-foreground">{step.date}</span>
							)}
						</p>
						{step.sublabel && (
							<p className="text-xs text-muted-foreground mt-0.5">{step.sublabel}</p>
						)}
						{step.ctaLabel && onStepCtaClick && (
							<div className="mt-3" data-testid={testIdPrefix && `${testIdPrefix}-cta-${step.id}`}>
								<button
									className="cursor-pointer bg-gradient-brand text-primary-foreground text-sm font-semibold px-4 py-2 relative before:absolute before:left-1/2 before:top-1/2 before:size-11 before:-translate-x-1/2 before:-translate-y-1/2 rounded-control transition-opacity hover:opacity-90 active:opacity-80"
									onClick={() => onStepCtaClick(step.id)}
								>
									{step.ctaLabel}
								</button>
							</div>
						)}
					</div>
				</div>
				{idx < steps.length - 1 && (
					<Connector fromState={step.state} toState={steps[idx + 1].state} />
				)}
			</div>
		))}
	</div>
);
